/**
 * Pedestrians — random cartoon civilians who cross the map.
 * Hit them = lose coins (server deducts via WS).
 */
import { OUTLINE, roundRect } from "./itemVisuals";

const WORLD_W = 1280;
const WORLD_H = 820;

const BODY_COLORS = ["#E63946", "#4A90E2", "#2E9D5E", "#9B59B6", "#F5B71B", "#FF6B6B", "#FF8C42"];
const SKIN_TONES = ["#F1C27D", "#E0AC69", "#C68642", "#8D5524", "#FFDBAC"];

export function initPedestrians() {
  return {
    list: [],
    lastSpawn: 0,
    spawnInterval: 4500, // ms between spawns
  };
}

export function spawnPedestrian(state) {
  // Spawn from edges (road entrances), walk toward opposite edge
  const side = Math.floor(Math.random() * 4);
  let x, y, vx, vy;
  const speed = 0.9 + Math.random() * 0.6;
  // Pedestrians mostly cross the main roads (horizontal and vertical bands)
  const roadBand = Math.random() < 0.7;
  if (side === 0) {
    x = -30;
    y = roadBand ? WORLD_H / 2 + (Math.random() - 0.5) * 120 : 120 + Math.random() * (WORLD_H - 240);
    vx = speed; vy = 0;
  } else if (side === 1) {
    x = WORLD_W + 30;
    y = roadBand ? WORLD_H / 2 + (Math.random() - 0.5) * 120 : 120 + Math.random() * (WORLD_H - 240);
    vx = -speed; vy = 0;
  } else if (side === 2) {
    x = roadBand ? WORLD_W / 2 + (Math.random() - 0.5) * 120 : 120 + Math.random() * (WORLD_W - 240);
    y = -30;
    vx = 0; vy = speed;
  } else {
    x = roadBand ? WORLD_W / 2 + (Math.random() - 0.5) * 120 : 120 + Math.random() * (WORLD_W - 240);
    y = WORLD_H + 30;
    vx = 0; vy = -speed;
  }
  state.list.push({
    id: `ped-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    x, y, vx, vy,
    shirt: BODY_COLORS[Math.floor(Math.random() * BODY_COLORS.length)],
    skin: SKIN_TONES[Math.floor(Math.random() * SKIN_TONES.length)],
    pants: Math.random() < 0.5 ? "#2a3e5a" : "#5a2a2a",
    hit: false,
    hitTimer: 0,
    panic: false,
    panicTimer: 0,
    wobble: Math.random() * Math.PI * 2,
    bob: Math.random() * Math.PI * 2,
  });
}

export function updatePedestrians(state, player, onHit) {
  const t = performance.now();
  if (t - state.lastSpawn > state.spawnInterval && state.list.length < 6) {
    spawnPedestrian(state);
    state.lastSpawn = t;
  }

  const alive = [];
  for (const p of state.list) {
    // panic near player
    const dp = Math.hypot(player.x - p.x, player.y - p.y);
    if (!p.hit && dp < 140) {
      p.panic = true;
      p.panicTimer = 25;
      // flee perpendicular to approach direction
      const ax = p.x - player.x, ay = p.y - player.y;
      const d = Math.hypot(ax, ay) || 1;
      p.vx = (ax / d) * 2.2;
      p.vy = (ay / d) * 2.2;
    } else if (p.panic) {
      p.panicTimer -= 1;
      if (p.panicTimer <= 0) p.panic = false;
    }

    p.x += p.vx;
    p.y += p.vy;
    p.wobble += 0.2;
    p.bob += 0.25;

    // collision with player car (not already hit)
    if (!p.hit && dp < 24) {
      p.hit = true;
      p.hitTimer = 55;
      p.vx = (p.x - player.x) * 0.12;
      p.vy = (p.y - player.y) * 0.12 - 3;
      onHit?.(p);
    }
    if (p.hit) {
      p.hitTimer -= 1;
      p.vy += 0.25; // gravity-like pull
      p.vx *= 0.98;
      if (p.hitTimer <= 0) continue; // remove
    }

    // off-screen removal
    if (p.x < -80 || p.x > WORLD_W + 80 || p.y < -80 || p.y > WORLD_H + 80) continue;
    alive.push(p);
  }
  state.list = alive;
}

export function drawPedestrian(ctx, p) {
  ctx.save();
  ctx.translate(p.x, p.y);

  // panicked speed lines
  if (p.panic && !p.hit) {
    ctx.strokeStyle = "rgba(0,0,0,0.5)";
    ctx.lineWidth = 2;
    for (let i = 0; i < 4; i++) {
      ctx.beginPath();
      ctx.moveTo(-18 + i * 4, 10 + i * 2);
      ctx.lineTo(-6 + i * 4, 8 + i * 2);
      ctx.stroke();
    }
  }

  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.3)";
  ctx.beginPath();
  ctx.ellipse(0, 14, 10, 3, 0, 0, Math.PI * 2);
  ctx.fill();

  const sway = p.hit ? 0 : Math.sin(p.wobble) * 0.2;
  const bobY = p.hit ? 0 : Math.sin(p.bob) * 2;

  ctx.rotate(sway);

  // legs (one forward, one back - walking pose)
  ctx.fillStyle = p.pants;
  const legOffset = p.hit ? 0 : Math.sin(p.bob) * 3;
  ctx.beginPath();
  roundRect(ctx, -6, 4 + bobY, 5, 10 + legOffset, 2);
  ctx.fill(); ctx.stroke();
  ctx.beginPath();
  roundRect(ctx, 1, 4 + bobY, 5, 10 - legOffset, 2);
  ctx.fill(); ctx.stroke();

  // body (shirt)
  ctx.fillStyle = p.shirt;
  ctx.beginPath();
  roundRect(ctx, -7, -6 + bobY, 14, 12, 4);
  ctx.fill(); ctx.stroke();

  // arms swing
  const armOffset = p.hit ? 0 : Math.sin(p.bob + Math.PI) * 3;
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  roundRect(ctx, -11, -4 + bobY + armOffset, 4, 8, 2);
  ctx.fill(); ctx.stroke();
  ctx.beginPath();
  roundRect(ctx, 7, -4 + bobY - armOffset, 4, 8, 2);
  ctx.fill(); ctx.stroke();

  // head
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  ctx.arc(0, -12 + bobY, 7, 0, Math.PI * 2);
  ctx.fill(); ctx.stroke();

  // eyes (big cartoon)
  if (p.hit) {
    // X_X
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(-4, -14 + bobY); ctx.lineTo(-1, -11 + bobY);
    ctx.moveTo(-1, -14 + bobY); ctx.lineTo(-4, -11 + bobY);
    ctx.moveTo(1, -14 + bobY); ctx.lineTo(4, -11 + bobY);
    ctx.moveTo(4, -14 + bobY); ctx.lineTo(1, -11 + bobY);
    ctx.stroke();
  } else {
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath(); ctx.arc(-2.5, -12 + bobY, 2, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.arc(2.5, -12 + bobY, 2, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    ctx.fillStyle = OUTLINE;
    ctx.beginPath(); ctx.arc(-2.5, -12 + bobY + (p.panic ? -0.5 : 0), 1, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(2.5, -12 + bobY + (p.panic ? -0.5 : 0), 1, 0, Math.PI * 2); ctx.fill();
  }

  // mouth
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 1.5;
  if (p.panic && !p.hit) {
    ctx.beginPath();
    ctx.arc(0, -9 + bobY, 2, 0, Math.PI * 2);
    ctx.stroke();
  } else if (p.hit) {
    // dazed
    ctx.beginPath();
    ctx.moveTo(-3, -9 + bobY);
    ctx.quadraticCurveTo(0, -7 + bobY, 3, -9 + bobY);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(-2, -9 + bobY);
    ctx.lineTo(2, -9 + bobY);
    ctx.stroke();
  }

  ctx.restore();

  // "!" bubble when panicked
  if (p.panic && !p.hit) {
    ctx.save();
    ctx.translate(p.x + 12, p.y - 22);
    ctx.fillStyle = "#FFFFFF";
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(0, 0, 9, 0, Math.PI * 2);
    ctx.fill(); ctx.stroke();
    ctx.fillStyle = "#E63946";
    ctx.font = "bold 14px 'Anton'";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("!", 0, 1);
    ctx.restore();
  }

  // Stars when just hit
  if (p.hit && p.hitTimer > 35) {
    ctx.save();
    ctx.translate(p.x, p.y - 20);
    const age = 55 - p.hitTimer;
    for (let i = 0; i < 5; i++) {
      const ang = (i / 5) * Math.PI * 2 + age * 0.1;
      const r = 6 + age * 0.4;
      ctx.fillStyle = "#FFD166";
      ctx.strokeStyle = OUTLINE;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      const px = Math.cos(ang) * r;
      const py = Math.sin(ang) * r - 4;
      ctx.arc(px, py, 3, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    }
    ctx.restore();
  }
}
